import React, { useContext, useEffect } from 'react';
import { Typography, Card } from '@arco-design/web-react';
import { standalonePagesStyles as styles } from '@/style';
import { GlobalContext } from '@/context';
import Logo from '@/assets/logo.svg';
import PageTitle from '@/components/PageTitle';
import { useHistory } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;

function Changelog() {
  const { theme } = useContext(GlobalContext) || { theme: 'light' };
  const history = useHistory();

  useEffect(() => {
    document.body.setAttribute('arco-theme', theme || 'light');
  }, [theme]);

  return (
    <div className={styles.standalone}>
      <PageTitle titleKey="menu.changeLog" />
      <div className={styles.header}>
        <a
          href="https://www.suwendata.com/"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.logoContainer}
          style={{ cursor: 'pointer', textDecoration: 'none' }}
        >
          <Logo />
          {/* <span className={styles.logoText}>速问</span> */}
        </a>
      </div>

      <div className={styles.container}>
        <Card className={styles.card}>
          <Title heading={2} className={styles.mainTitle}>
            更新日志
          </Title>

          <div className={styles.section}>
            <Title heading={4}>关于速问</Title>
            <Paragraph className={styles.intro}>
              欢迎查阅速问的更新日志。在这里，您可以了解我们平台的最新更新、改进和新增功能。我们不断努力提升您的使用体验，并为制药行业洞察提供更好的工具。
            </Paragraph>

            <Paragraph className={styles.intro}>
              <strong>速问</strong>
              是一个面向医疗行业的专业大数据平台，致力于为医疗从业者、制药企业、投资机构和政策制定者提供全面、精准的行业洞察。我们深度整合了政策数据、市场化数据、价格数据、销售数据、临床信息以及准入数据等多种核心资源，支持用户根据药品、企业、地区等多个维度灵活筛选和分析（部分功能后续上线）。
            </Paragraph>

            <Paragraph className={styles.intro}>
              平台核心功能包括：<strong>政策智能查询与解读</strong>、
              <strong>深度搜索与分析</strong>、<strong>AI智能问答系统</strong>、
              <strong>可视化图表展示</strong>、<strong>政策订阅与监控</strong>、
              <strong>影响力分析</strong>以及
              <strong>基于模板的PPT/PDF报告生成</strong>
              等。通过先进的人工智能技术和大数据分析能力，速问帮助您高效决策、洞悉市场趋势、优化业务布局，成为您在医疗行业探索与创新路上的得力助手。
            </Paragraph>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.4.1</Title>
            <Paragraph className={styles.date}>2026年2月13日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>政策详情附件逻辑优化</b>
                ：对齐附件类型处理逻辑，未知类型附件（type
                0）支持下载但不可预览，无效附件（type -1）不可下载和预览
              </li>
              <li>
                <b>订阅推送稳定性修复</b>
                ：修复订阅推送重复执行的问题，确保通知仅发送一次
              </li>
              <li>
                <b>加载动画体验优化</b>
                ：新增页面加载动画样式，提升等待过程中的用户体验
              </li>
              <li>
                <b>图片上传模块重构</b>
                ：图片存储支持按年/月/日目录结构自动归档，便于管理和维护
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.4.0</Title>
            <Paragraph className={styles.date}>2025年12月2日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>政策查询搜索体验优化</b>
                ：搜索时新增友好的加载提示「正在全库深度检索中，请稍候...」，让您清楚了解搜索进度
              </li>
              <li>
                <b>搜索准确性提升</b>
                ：优化了全文检索引擎，提高药品名称等专业术语的搜索命中率，支持更灵活的词汇匹配
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.3.9</Title>
            <Paragraph className={styles.date}>2025年11月30日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>医保目录数据查询上线</b>
                ：新增国家医保信息平台数据接入，提供全面的医保相关数据查询能力：
                <ul>
                  <li>
                    <b>药品目录查询</b>
                    ：支持按药品名称、医保编码等条件查询国家医保药品目录，查看药品的医保类别、限定支付范围等详细信息
                  </li>
                  <li>
                    <b>医疗机构查询</b>
                    ：查询全国医保定点医疗机构信息，支持按地区、机构名称等筛选
                  </li>
                  <li>
                    <b>定点药店查询</b>
                    ：查询医保定点零售药店信息，方便了解药店的医保服务状态
                  </li>
                  <li>
                    <b>医用耗材查询</b>
                    ：支持医保医用耗材目录的查询和浏览
                  </li>
                  <li>
                    <b>区域筛选</b>
                    ：支持省、市、区县三级地区联动筛选，快速定位目标区域数据
                  </li>
                </ul>
                <span
                  style={{ color: 'var(--color-text-3)', fontSize: '12px' }}
                >
                  入口位置：导航菜单「医保数据」下可找到相关功能入口
                </span>
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.3.8</Title>
            <Paragraph className={styles.date}>2025年11月19日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>政策问答引用展示全面升级</b>
                ：重新设计政策问答的引用展示方式，从抽屉模式升级为左右分栏布局，让您可以同时查看答案和参考资料：
                <ul>
                  <li>
                    <b>左右分栏布局</b>
                    ：左侧显示问答对话，右侧固定展示引用详情，无需来回切换，提升阅读效率
                  </li>
                  <li>
                    <b>始终可见</b>
                    ：滚动页面时，右侧参考面板始终保持在视野内，方便随时查阅
                  </li>
                  <li>
                    <b>灵活调整</b>
                    ：支持拖拽调整右侧面板宽度，系统自动记住您的偏好设置
                  </li>
                  <li>
                    <b>流畅动画</b>
                    ：右侧面板打开和关闭采用平滑过渡动画，提升视觉体验
                  </li>
                  <li>
                    <b>参考来源标号优化</b>
                    ：优化引用卡片标号的视觉设计，采用主题色配色，增强悬停交互效果，避免标号与标题重叠
                  </li>
                  <li>
                    <b>文档类型智能识别</b>
                    ：自动识别并标注 PDF、Word、Excel、PPT 等文档格式，彩色标签显示在"党政机关"标签旁边，帮助用户快速区分网页链接和文档下载链接
                  </li>
                </ul>
                <span
                  style={{ color: 'var(--color-text-3)', fontSize: '12px' }}
                >
                  使用场景：特别适合需要深度研读政策内容、对照原文核实信息、或进行跨文档比对分析的场景，显著提升政策研究效率
                </span>
              </li>
              <li>
                <b>全网搜索语言优化</b>
                ：优化全网搜索的语言处理能力，确保回答内容始终使用中文：
                <ul>
                  <li>
                    <b>语言一致性提升</b>
                    ：系统现在能够更好地控制回答语言，避免出现中英文混杂的情况
                  </li>
                  <li>
                    <b>用户体验改进</b>
                    ：全网搜索结果统一使用中文呈现，提供更流畅的阅读体验
                  </li>
                  <li>
                    <b>智能语言识别</b>
                    ：即使搜索到的资料包含多种语言，系统也会以中文方式整理和呈现关键信息
                  </li>
                </ul>
                <span
                  style={{ color: 'var(--color-text-3)', fontSize: '12px' }}
                >
                  注意：全网搜索支持专业模型和深度研究模型两种选择。专业模型的语言一致性效果最佳，深度研究模型可能会出现语言一致性下降的情况，我们会持续优化
                </span>
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.3.7</Title>
            <Paragraph className={styles.date}>2025年11月18日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>政策问答响应速度优化</b>
                ：显著提升问题提交后的即时反馈体验：
                <ul>
                  <li>
                    <b>即时响应</b>
                    ：用户提交问题后立即显示问题内容和处理进度，无需等待后端处理，响应时间从 500ms-1s 缩短至约 100ms
                  </li>
                  <li>
                    <b>流程透明</b>
                    ：实时展示合规检查、意图分析、资料搜索、生成回答等各个处理阶段，让用户清楚了解系统工作状态
                  </li>
                  <li>
                    <b>体验提升</b>
                    ：消除等待感，系统感觉更加灵敏和响应迅速
                  </li>
                </ul>
              </li>
              <li>
                <b>界面交互优化</b>
                ：修复导出按钮在回答生成过程中的状态问题，确保在 AI 回答未完成时正确禁用导出功能，避免误操作
              </li>
              <li>
                <b>历史配置兼容性修复</b>
                ：解决用户本地存储的旧版配置与当前版本不兼容的问题。自动清理无效的历史配置数据，确保系统在全网搜索模式下正常运行，避免因配置冲突导致的功能异常
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.3.6</Title>
            <Paragraph className={styles.date}>2025年11月16日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>全网搜索引用展示优化</b>
                ：持续优化全网搜索的引用展示细节，提升视觉体验和交互便捷性：
                <ul>
                  <li>
                    <b>视觉优化</b>
                    ：引用标签增加淡雅底色，hover 时加深，提升视觉层次感和交互反馈
                  </li>
                  <li>
                    <b>Favicon 样式统一</b>
                    ：所有 Favicon 图标统一为圆形设计，边框颜色与背景色协调一致
                  </li>
                  <li>
                    <b>来源详情优化</b>
                    ：抽屉中引用标题限制为单行显示，超出部分显示省略号，保持界面整洁
                  </li>
                  <li>
                    <b>交互体验提升</b>
                    ：点击整个引用卡片即可跳转到原文，无需精确点击标题，操作更便捷
                  </li>
                  <li>
                    <b>权威来源标识</b>
                    ：自动识别政府官方网站（gov.cn），添加醒目的"党政机关"橙红色标签，帮助用户快速识别权威信息源
                  </li>
                </ul>
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.3.5</Title>
            <Paragraph className={styles.date}>2025年11月12日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>全网搜索引用展示优化</b>
                ：全面改版全网搜索的引用展示方式，采用更现代的设计：
                <ul>
                  <li>
                    引用以网站 Favicon 图标叠加形式展示，点击后打开抽屉查看详情
                  </li>
                  <li>
                    自动识别来源类型并智能标注（如"互联网"），操作按钮移至同一行右侧
                  </li>
                </ul>
              </li>
              <li>
                <b>对话历史云端同步</b>
                ：政策问答系统对话记录现已支持云端自动保存，确保您的问答历史永久保存：
                <ul>
                  <li>
                    <b>自动云端备份</b>
                    ：每次对话自动保存到云端，无需手动操作
                  </li>
                  <li>
                    <b>刷新不丢失</b>
                    ：页面刷新或重新打开后，所有历史对话自动恢复
                  </li>
                  <li>
                    <b>完整信息保留</b>
                    ：对话的所有内容完整保存，包括AI回答、政策引用链接、推理思考过程、相关推荐问题、全网搜索来源等
                  </li>
                  <li>
                    <b>跨设备访问</b>
                    ：同一账号可在不同设备查看完整对话历史
                  </li>
                </ul>
                <span
                  style={{ color: 'var(--color-text-3)', fontSize: '12px' }}
                >
                  使用建议：现在您可以放心地进行深度研究和长期追踪，所有对话都会妥善保存。对于重要的政策解读和分析结果，可以随时回溯查看
                </span>
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.3.4</Title>
            <Paragraph className={styles.date}>2025年11月11日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>全网搜索能力正式上线</b>
                ：政策问答系统新增全网搜索模式，突破政策库限制，实时获取互联网最新资讯：
                <ul>
                  <li>
                    <b>实时信息获取</b>
                    ：自动搜索全网医疗政策相关的最新资讯、行业动态、专家解读等内容，确保信息时效性
                  </li>
                  <li>
                    <b>智能来源筛选</b>
                    ：系统自动评估信息来源的权威性和可靠性，优先展示官方网站、权威媒体和专业平台的内容
                  </li>
                  <li>
                    <b>学术级引用标注</b>
                    ：回答中的引用以上标形式[1][2]显示，类似学术论文引用格式，点击即可跳转到原文链接
                  </li>
                  <li>
                    <b>来源信息展示</b>
                    ：每条引用显示来源网站标题、发布日期和URL，便于用户核实和深入阅读
                  </li>
                  <li>
                    <b>搜索过程可视化</b>
                    ：展示安全检查、意图分析、资料搜索等步骤进度，让搜索过程透明可见
                  </li>
                  <li>
                    <b>智能模式切换</b>
                    ：系统根据问题类型自动选择政策库搜索或全网搜索，也支持用户手动指定搜索方式
                  </li>
                </ul>
                <span
                  style={{ color: 'var(--color-text-3)', fontSize: '12px' }}
                >
                  使用场景：特别适合查询最新政策动态、行业热点事件、政策影响分析、专家观点等实时性要求较高的内容。全网搜索与政策库搜索互为补充，为您提供更全面的信息视角
                </span>
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.3.3</Title>
            <Paragraph className={styles.date}>2025年11月6日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>超长政策内容解析支持</b>
                ：全面升级政策内容处理能力，突破传统文档长度限制：
                <ul>
                  <li>
                    <b>海量内容收录</b>
                    ：支持如药品目录、医保目录等超长政策文档的完整收录，单文档可处理数十万字内容
                  </li>
                  <li>
                    <b>智能分段预览</b>
                    ：超长内容自动分段处理，提供流畅的内容预览体验，无需担心页面卡顿
                  </li>
                  <li>
                    <b>精准智能问答</b>
                    ：针对超长文档优化检索算法，快速定位相关内容，支持对药品目录等大型政策文件的智能问答
                  </li>
                  <li>
                    <b>性能优化</b>
                    ：采用增量加载和智能缓存技术，确保超长文档的流畅访问和快速响应
                  </li>
                </ul>
                <span
                  style={{ color: 'var(--color-text-3)', fontSize: '12px' }}
                >
                  此功能特别适用于医保目录、药品目录、诊疗项目目录等包含大量数据的政策文件
                </span>
              </li>
              <li>
                <b>政策详情独立页面</b>
                ：新增政策详情独立页面访问方式，支持直接通过 URL 访问和分享：
                <ul>
                  <li>支持通过独立 URL 直接访问政策详情，便于分享和收藏</li>
                  <li>页面内容以卡片形式展示，工具栏更简洁</li>
                  <li>知识库、工作台等页面政策链接已更新为独立页面地址</li>
                  <li>"复制链接"功能统一复制独立页面 URL</li>
                  <li>原有抽屉模式不受影响，可根据场景选择使用</li>
                </ul>
              </li>
              <li>
                <b>可视化分析能力升级</b>
                ：新增多种结构化图表支持，提升政策分析的直观性：
                <ul>
                  <li>
                    新增流程图、思维导图、时间线、状态图等多种 Mermaid
                    图表类型，帮助更好地理解政策流程和关系结构
                  </li>
                  <li>
                    优化数据图表生成逻辑：仅在上下文存在明确数字时生成数据可视化图表，避免不准确的图表展示
                  </li>
                </ul>
              </li>
              <li>
                <b>流式回答体验优化</b>
                ：优化政策引用的渲染时机，在回答完成后再渲染交互元素，消除流式输出时的页面抖动
              </li>
              <li>
                <b>政策详情页面体验优化</b>
                ：优化政策详情抽屉的内容展示，避免超长内容影响浏览体验：
                <ul>
                  <li>
                    <b>结构化内容展开/收起</b>
                    ：当结构化内容超过一定高度时，自动显示"展开更多"按钮，支持手动控制内容显示
                  </li>
                  <li>
                    <b>附件列表展开/收起</b>
                    ：当附件数量较多、列表高度超过限制时，自动提供展开/收起功能，保持页面整洁
                  </li>
                  <li>
                    <b>智能高度检测</b>
                    ：系统自动检测内容和附件列表的实际高度，仅在需要时显示展开按钮
                  </li>
                  <li>
                    <b>平滑过渡动画</b>
                    ：展开和收起操作采用流畅的动画效果，提升视觉体验
                  </li>
                  <li>
                    <b>渐变遮罩提示</b>
                    ：收起状态下底部显示渐变遮罩，直观提示用户有更多内容可查看
                  </li>
                </ul>
                <span
                  style={{ color: 'var(--color-text-3)', fontSize: '12px' }}
                >
                  此优化特别适合查看包含大量结构化内容或多个附件的政策文档，有效避免页面过长带来的浏览困难
                </span>
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.3.1</Title>
            <Paragraph className={styles.date}>2025年10月21日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>账号下线无反馈优化</b>
                ：优化了当多平台登录时进入页面无反馈，提高了体验性和界面可读性
              </li>
              <li>
                <b>多平台登录优化</b>
                ：实现了同一账号可以双平台（速问与开放平台）登录，提高了用户体验和平台可用性
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.3.0</Title>
            <Paragraph className={styles.date}>2025年10月20日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>政策查询日期筛选优化</b>
                ：优化了政策查询页面的日期筛选选项，提供更合理的时间范围选择：
                <ul>
                  <li>
                    <b>筛选选项调整</b>
                    ：将日期快捷选项从"近3天、近7天、近30天"调整为"30天内、一年内"，更符合政策查询的实际使用场景
                  </li>
                  <li>
                    <b>保留灵活性</b>
                    ：继续支持"不限"和"自定义"选项，满足不同的查询需求
                  </li>
                </ul>
              </li>
              <li>
                <b>政策查询界面简化</b>
                ：精简政策查询页面的筛选条件，让界面更加简洁易用：
                <ul>
                  <li>
                    <b>精简筛选字段</b>
                    ：暂时移除"信息源"、"分类"和"相关度"筛选条件，聚焦核心查询功能
                  </li>
                  <li>
                    <b>命名优化</b>
                    ：将"文件重要程度"统一调整为"影响力"，表述更加清晰直观
                  </li>
                  <li>
                    <b>按钮文案优化</b>
                    ：将"批量操作"按钮调整为"选择"，表意更加简洁明了
                  </li>
                  <li>
                    <b>列设置优化</b>
                    ：暂时隐藏"列设置"按钮，简化界面操作；优化默认显示列为"日期、相关度、影响力、关键词"，突出核心信息
                  </li>
                </ul>
              </li>
              <li>
                <b>政策操作按钮优化</b>
                ：优化政策列表操作栏布局，提升常用功能的访问效率：
                <ul>
                  <li>
                    <b>独立按钮显示</b>
                    ：将"原文"和"收藏"从操作下拉菜单中移出，作为独立按钮显示在"查看"按钮右侧
                  </li>
                  <li>
                    <b>快速访问</b>
                    ：用户无需点击下拉菜单即可直接访问原文链接和收藏功能，操作更加便捷
                  </li>
                  <li>
                    <b>收藏状态可视化</b>
                    ：收藏按钮显示星标图标，已收藏的政策显示金色实心星标，未收藏显示空心星标
                  </li>
                </ul>
              </li>
              <li>
                <b>功能精简</b>
                ：移除复制UUID功能，简化操作菜单，减少不常用功能的干扰
              </li>
              <li>
                <b>影响力分析雷达图优化</b>
                ：调整政策详情页影响力分析雷达图的维度展示顺序，提升信息展示逻辑：
                <ul>
                  <li>
                    <b>顺序调整</b>
                    ：将雷达图维度顺序调整为"部门级别、政策范围、创新性、执行力度、市场影响、可持续性"，更符合政策分析的逻辑层次
                  </li>
                  <li>
                    <b>一致性优化</b>
                    ：同步调整雷达图、对比模式和下方详细卡片的展示顺序，确保视觉呈现统一
                  </li>
                </ul>
              </li>
              <li>
                <b>字段命名优化</b>
                ：将政策详情页基本信息中的"地区"字段调整为"效力"，更准确地反映该字段的实际含义
              </li>
              <li>
                <b>快捷入口优化</b>
                ：在页面右上角添加快捷入口按钮，提升常用功能的可访问性：
                <ul>
                  <li>
                    <b>内容纠错反馈</b>
                    ：新增纠错反馈入口，用户可快速提交内容问题和改进建议
                  </li>
                  <li>
                    <b>速问知识库</b>
                    ：新增知识库快捷入口，方便用户查阅平台使用指南和帮助文档
                  </li>
                </ul>
              </li>
              <li>
                <b>权限验证重试机制</b>
                ：优化页面刷新时的权限验证体验，显著降低权限错误提示的频率：
                <ul>
                  <li>
                    <b>智能自动重试</b>
                    ：当权限验证失败时，系统会自动重试最多3次，每次间隔1.5秒，有效应对临时网络波动
                  </li>
                  <li>
                    <b>加载状态优化</b>
                    ：用户信息加载时显示友好的加载提示，避免过早显示权限错误页面
                  </li>
                  <li>
                    <b>手动重试选项</b>
                    ：403页面提供"重试"按钮，用户可主动触发权限验证
                  </li>
                  <li>
                    <b>实时状态反馈</b>
                    ：显示重试次数和验证进度，让用户了解系统正在处理
                  </li>
                  <li>
                    <b>智能跳转</b>
                    ：验证成功后自动返回原页面，无需手动导航
                  </li>
                </ul>
                <span
                  style={{ color: 'var(--color-text-3)', fontSize: '12px' }}
                >
                  此优化结合了网络层和UI层的双重重试保障，大幅提升了刷新页面时的用户体验
                </span>
              </li>
              <li>
                <b>政策对比页面全新升级</b>
                ：将政策对比从嵌套抽屉优化为独立页面，提供更宽敞的对比空间和更好的用户体验：
                <ul>
                  <li>
                    <b>独立页面访问</b>
                    ：在"医疗数据"菜单下新增"政策对比"入口，与"政策查询"同级，可独立访问
                  </li>
                  <li>
                    <b>左右布局</b>
                    ：左侧固定宽度展示比对政策列表，右侧弹性区域展示可视化分析和智能分析结果
                  </li>
                  <li>
                    <b>可视化分析增强</b>
                    ：包含影响力雷达图和维度对比表格，支持多政策横向对比
                  </li>
                  <li>
                    <b>智能分析结果</b>
                    ：支持AI流式输出比对分析，实时展示分析进度，Markdown格式呈现
                  </li>
                  <li>
                    <b>界面细节优化</b>
                    ：优化空状态居中显示，提升表格可读性，改善整体视觉体验
                  </li>
                </ul>
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.2.9</Title>
            <Paragraph className={styles.date}>2025年10月15日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>开放平台正式发布</b>
                ：全新推出速问开放平台，为开发者提供完善的 API 接口和管理工具：
                <ul>
                  <li>
                    <b>API 调试功能</b>
                    ：提供完整的 API 在线调试工具，支持接口测试和参数验证：
                    <ul>
                      <li>
                        <b>API 文档查看</b>
                        ：完整的接口文档，包含请求参数、响应格式和调用示例
                      </li>
                      <li>
                        <b>在线调试</b>
                        ：支持直接在页面中测试 API 接口，实时查看请求和响应数据
                      </li>
                      <li>
                        <b>参数输入</b>
                        ：智能参数输入模块，自动验证参数格式和必填项
                      </li>
                      <li>
                        <b>请求封装</b>
                        ：统一的请求封装和错误处理机制，提升调试效率
                      </li>
                    </ul>
                  </li>
                  <li>
                    <b>开发者控制台</b>
                    ：专业的 API 密钥管理和使用监控平台：
                    <ul>
                      <li>
                        <b>API 密钥管理</b>
                        ：创建、查看和管理您的 API 密钥，支持多密钥管理
                      </li>
                      <li>
                        <b>密钥安全</b>
                        ：支持密钥的显示/隐藏切换，保护您的密钥安全
                      </li>
                      <li>
                        <b>最新密钥显示</b>
                        ：自动显示最新创建的密钥，方便快速使用
                      </li>
                      <li>
                        <b>无密钥引导</b>
                        ：当没有密钥时，提供创建链接引导，提升用户体验
                      </li>
                    </ul>
                  </li>
                  <li>
                    <b>部署支持</b>
                    ：完善的部署配置，支持 Docker 容器化部署
                  </li>
                  <li>
                    <b>体验优化</b>
                    ：修复刷新重定向问题，优化 API
                    调试域名配置，提升整体使用体验
                  </li>
                </ul>
                <span
                  style={{ color: 'var(--color-text-3)', fontSize: '12px' }}
                >
                  开放平台让您可以将速问的强大能力集成到自己的应用中，打造更多医疗政策智能应用
                </span>
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.2.8</Title>
            <Paragraph className={styles.date}>2025年8月11日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>评估维度优化</b>
                ：将政策影响力分析中的"机制创新"维度更新为"可持续性"维度：
                <ul>
                  <li>
                    <b>维度名称变更</b>
                    ：原"机制创新"维度正式更名为"可持续性"，更准确反映政策的长期影响和持续发展能力
                  </li>
                  <li>
                    <b>评估重点调整</b>
                    ：评估重点从政策的创新性转向政策实施的可持续性和长期稳定性
                  </li>
                  <li>
                    <b>指标体系优化</b>
                    ：调整相关评分算法，更好地衡量政策在环境友好性、经济可行性和社会可接受性等方面的表现
                  </li>
                  <li>
                    <b>界面同步更新</b>
                    ：所有相关界面和图表中的维度名称已同步更新，保持系统一致性
                  </li>
                </ul>
                <span
                  style={{ color: 'var(--color-text-3)', fontSize: '12px' }}
                >
                  背景说明：此次调整基于用户反馈和行业发展趋势，更好地体现政策评估的全面性和前瞻性
                </span>
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.2.7</Title>
            <Paragraph className={styles.date}>2025年7月30日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>政策查询界面优化</b>
                ：全面升级政策列表展示逻辑，提供更智能的文件管理体验：
                <ul>
                  <li>
                    <b>相同标题文件智能合并</b>
                    ：自动识别并合并标题完全相同的政策文件，减少重复信息干扰，让列表更加清晰简洁
                  </li>
                  <li>
                    <b>便捷的下拉查看</b>
                    ：鼠标悬停在合并后的标题上即可显示所有相关文件，每个文件都显示文号和日期，方便快速区分和选择
                  </li>
                  <li>
                    <b>视觉标识优化</b>
                    ：合并的条目会显示文件数量标签，让您一眼就能看出该标题下包含多少个相关文件
                  </li>
                  <li>
                    <b>保持原有交互</b>
                    ：所有文件都可以正常点击查看详情，不影响现有的操作习惯
                  </li>
                </ul>
                <span
                  style={{ color: 'var(--color-text-3)', fontSize: '12px' }}
                >
                  使用场景：特别适合处理政策修订、补充通知等会产生多个相同标题文件的情况，大幅提升政策浏览和管理效率
                </span>
              </li>
              <li>
                <b>政策问答界面响应式优化</b>
                ：改进问答类型选择器在小屏幕设备上的显示效果：
                <ul>
                  <li>
                    <b>智能图标模式</b>
                    ：在对话进行中，问答类型选择器（简洁、深入、研究）自动切换为图标模式，节省界面空间
                  </li>
                  <li>
                    <b>保留交互提示</b>
                    ：即使在图标模式下，鼠标悬停仍会显示完整的功能说明，确保用户了解每个选项的含义
                  </li>
                  <li>
                    <b>优化移动端体验</b>
                    ：解决了小屏幕设备上按钮被挤下去的问题，确保所有功能按钮始终保持在同一行
                  </li>
                </ul>
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.2.6</Title>
            <Paragraph className={styles.date}>2025年7月29日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>内容审核系统升级</b>
                ：全面升级政策问答的内容审核机制，提升系统专注度和用户体验：
                <ul>
                  <li>
                    <b>智能相关性判断</b>
                    ：系统现在能够智能识别问题是否与医疗政策相关，自动过滤无关查询，让平台更聚焦于专业医疗政策服务
                  </li>
                  <li>
                    <b>友好引导提示</b>
                    ：对于非医疗政策相关的问题，系统会给出清晰友好的提示，引导用户提出相关领域的问题
                  </li>
                  <li>
                    <b>支持的查询范围</b>
                    ：涵盖医疗政策、药品监管、医保制度、医疗器械、中医药发展、公共卫生等所有医疗政策相关话题
                  </li>
                  <li>
                    <b>处理效率提升</b>
                    ：通过前置审核机制，避免系统处理无关查询，整体响应效率提升约20%
                  </li>
                </ul>
                <span
                  style={{ color: 'var(--color-text-3)', fontSize: '12px' }}
                >
                  技术说明：该功能采用先进的AI语义理解技术，能够准确判断用户查询意图，确保平台资源用于专业医疗政策服务
                </span>
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.2.5</Title>
            <Paragraph className={styles.date}>2025年7月12日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>相关度评分区间优化</b>
                ：修正了政策相关度评分的星级区间划分，确保所有评分都能被正确查询：
                <ul>
                  <li>
                    调整评分区间为均匀的20分间隔，每个星级对应更合理的分数范围
                  </li>
                  <li>解决了低分内容（0-19分）无法被1星查询覆盖的问题</li>
                  <li>
                    扩大了5星评分的覆盖范围（80-100分），让更多高质量内容被正确识别
                  </li>
                </ul>
              </li>
              <li>
                <b>政策列表展示增强</b>
                ：新增文件重要程度和文件性质的列设置功能，提升政策信息的可视化管理：
                <ul>
                  <li>
                    <b>文件重要程度列</b>
                    ：直观展示政策文件的重要性级别，帮助用户快速识别关键政策
                  </li>
                  <li>
                    <b>文件性质列</b>
                    ：显示政策文件的具体类型和属性，便于分类管理和筛选
                  </li>
                  <li>
                    <b>自定义列显示</b>
                    ：用户可根据需求自由选择显示或隐藏这些列，打造个性化的政策浏览界面
                  </li>
                  <li>
                    <b>列宽自适应</b>
                    ：智能调整列宽，确保在不同屏幕尺寸下都能获得最佳浏览体验
                  </li>
                </ul>
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.2.4</Title>
            <Paragraph className={styles.date}>2025年7月6日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>医疗搜索分词功能全面升级</b>
                ：引入基于AI的智能医疗术语分词引擎，大幅提升搜索精准度：
                <ul>
                  <li>
                    <b>专业术语智能识别</b>
                    ：AI自动识别并保持完整的医疗专业术语，如&ldquo;吡非尼酮&rdquo;、&ldquo;带量采购&rdquo;、&ldquo;DRG支付&rdquo;等，避免被错误拆分
                  </li>
                  <li>
                    <b>医药词汇精准保护</b>
                    ：药品名称、疾病名称、医疗器械、政策术语等专业词汇完整保留，确保搜索准确性
                  </li>
                  <li>
                    <b>智能停用词过滤</b>
                    ：自动过滤&ldquo;的&rdquo;、&ldquo;了&rdquo;、&ldquo;和&rdquo;等无意义词汇，让搜索更聚焦于关键内容
                  </li>
                  <li>
                    <b>AI降级保护机制</b>
                    ：当AI服务不可用时，自动降级到传统分词方式，确保搜索功能始终可用
                  </li>
                  <li>
                    <b>搜索准确率提升40%</b>
                    ：通过智能分词技术，政策搜索和内容检索的准确率显著提升，减少无关结果
                  </li>
                </ul>
                <span
                  style={{ color: 'var(--color-text-3)', fontSize: '12px' }}
                >
                  技术说明：该功能采用专门训练的医疗行业AI模型，结合医药术语词典，实现更精准的中文分词效果
                </span>
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.2.3</Title>
            <Paragraph className={styles.date}>2025年6月22日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>图表可视化全面升级</b>
                ：大幅提升数据可视化能力，让您的数据分析更直观、更专业：
                <ul>
                  <li>
                    <b>稳定性提升</b>
                    ：优化了图表生成引擎，大幅减少图表显示错误，即使遇到特殊数据格式也能正确渲染
                  </li>
                  <li>
                    <b>数学公式兼容性增强</b>
                    ：图表与数学公式现在能够完美共存，在包含复杂统计分析的报告中，公式和图表都能正确显示
                  </li>
                </ul>
                <span
                  style={{ color: 'var(--color-text-3)', fontSize: '12px' }}
                >
                  此次升级特别适合需要进行复杂数据分析和展示的用户，让您的分析报告更加专业和易懂
                </span>
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.2.2</Title>
            <Paragraph className={styles.date}>2025年6月16日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>政策问答智能切片优化</b>
                ：全新升级长文档处理技术，让政策查询更精准、响应更快速：
                <ul>
                  <li>
                    <b>智能定位</b>
                    ：采用先进的文档切片技术，直接定位到与您问题最相关的政策段落，不再需要在冗长的文档中大海捞针
                  </li>
                  <li>
                    <b>上下文完整</b>
                    ：智能获取相关段落的前后文内容，确保您看到的政策条款解释完整、准确，避免断章取义
                  </li>
                  <li>
                    <b>超长文档支持</b>
                    ：即使面对数万字的政策文件或复杂附件，系统也能快速精准定位相关内容，大幅提升查询效率
                  </li>
                  <li>
                    <b>混合检索策略</b>
                    ：结合语义理解和关键词匹配双重技术，确保既能理解您的查询意图，又不会错过重要的政策条款
                  </li>
                  <li>
                    <b>性能提升</b>
                    ：相比传统的全文检索，新技术将响应速度提升
                    30%，同时返回内容的相关性提高 50%
                  </li>
                </ul>
                <span
                  style={{ color: 'var(--color-text-3)', fontSize: '12px' }}
                >
                  技术说明：该功能特别适用于查询超长政策文件、复杂法规条款、详细实施细则等场景
                </span>
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.2.1</Title>
            <Paragraph className={styles.date}>2025年6月3日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>医疗政策报告生成</b>
                ：全新推出智能报告生成功能，让您的政策研究成果更专业、更易分享：
                <ul>
                  <li>
                    <b>一键导出</b>
                    ：直接从政策问答会话中生成专业报告，无需手动整理
                  </li>
                  <li>
                    <b>多种格式支持</b>
                    ：提供PDF报告和PPT演示文稿两种导出格式，满足不同场景需求
                    <ul>
                      <li>
                        <b>PDF报告</b>
                        ：适合深度研究和存档，包含完整的分析内容和引用
                      </li>
                      <li>
                        <b>PPT演示文稿</b>
                        ：适合会议汇报和分享，重点突出，视觉效果出色
                      </li>
                    </ul>
                  </li>
                  <li>
                    <b>内容完整</b>
                    ：自动整合问答对话、政策引用、图表分析等完整内容
                  </li>
                  <li>
                    <b>样式专业</b>
                    ：采用专业模板设计，包含封面、目录、正文等完整结构
                  </li>
                  <li>
                    <b>智能优化</b>：AI 自动优化报告结构和内容表述，提升报告质量
                  </li>
                </ul>
              </li>
              <li>
                <b>数学公式渲染全面升级</b>
                ：数据智能板块全面支持 KaTeX
                数学公式渲染，为医药数据分析提供更专业的数学表达能力：
                <ul>
                  <li>
                    <b>智能识别</b>
                    ：系统自动识别问答内容中的数学表达式，并以专业的数学排版格式展示
                  </li>
                  <li>
                    <b>药物动力学模型</b>：精确展示药物浓度-时间曲线公式，支持
                    PK/PD 模型分析
                  </li>
                  <li>
                    <b>统计分析公式</b>
                    ：支持复杂的生物统计学计算表达，包括相关性分析、回归模型等
                  </li>
                  <li>
                    <b>剂量计算公式</b>
                    ：清晰呈现药物剂量换算和调整公式，辅助临床用药决策
                  </li>
                  <li>
                    <b>临床试验设计</b>
                    ：展示样本量计算、统计功效分析等试验设计公式
                  </li>
                </ul>
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.2.0</Title>
            <Paragraph className={styles.date}>2025年5月23日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>深度搜索功能（BETA）</b>
                ：推出基于医药政策知识库的深度搜索能力，为您提供更精准、更全面的医药政策解析。
              </li>
              <li>
                <b>多阶段思考模式</b>
                ：深度搜索采用创新的多阶段思考框架：
                <ul>
                  <li>问题理解：精准把握用户意图和查询核心</li>
                  <li>资料搜索：全面收集相关政策文件和背景信息</li>
                  <li>信息分析：深入挖掘政策含义和潜在影响</li>
                  <li>答案探索：综合多维度信息生成候选答案</li>
                  <li>结果评估：严格筛选和验证最终解析结果</li>
                </ul>
              </li>
              <li>
                <b>自动迭代优化</b>
                ：系统会自动迭代完善搜索结果，直至获得最精准答案，每次深度搜索需要3~5分钟处理时间。
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.1.9</Title>
            <Paragraph className={styles.date}>2025年5月18日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>政策问答可视化增强</b>
                ：全新支持图表显示功能，系统会根据问题内容智能渲染适合的图表，提升数据可视化体验。您还可以通过指令&ldquo;显示某某图表&rdquo;来主动请求特定图表类型。
              </li>
              <li>
                <b>多样化图表支持</b>
                ：全面支持多种图表类型，包括：
                <ul>
                  <li>饼图：直观展示数据占比和分布情况</li>
                  <li>面积图：展示数据随时间变化的趋势和累积效应</li>
                  <li>折线图：清晰呈现数据变化趋势和走向</li>
                  <li>柱状图：方便进行数据大小和分类比较</li>
                  <li>雷达图：多维度对比不同政策影响范围</li>
                  <li>散点图：分析不同数据点之间的相关性</li>
                  <li>热力图：直观展示数据密度和分布热度</li>
                  <li>漏斗图：分析政策执行阶段和转化情况</li>
                  <li>玫瑰图：优雅展示周期性数据和比例关系</li>
                </ul>
              </li>
              <li>
                <b>交互式图表体验</b>
                ：所有图表支持悬停查看详情、缩放和导出功能，便于深入分析和分享。
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.1.8</Title>
            <Paragraph className={styles.date}>2025年5月17日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>政策订阅增强</b>
                ：订阅功能全面升级，除标题与正文命中外，新增对政策文章附件标题与正文的命中支持，大幅提升监控覆盖范围。
              </li>
              <li>
                <b>用户体验优化</b>
                ：优化了回答中的自动滚动逻辑，页面滚动更加丝滑，提升整体交互体验。
              </li>
              <li>
                <b>政策问答提速</b>
                ：启用并行查询技术，并针对附件的索引速度做专项优化，显著提升问答响应速度。
              </li>
              <li>
                <b>政策查询排序增强</b>
                ：新增日期、相关度与影响力排序功能，让用户可以更灵活地获取所需政策信息。
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.1.7</Title>
            <Paragraph className={styles.date}>2025年5月16日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>政策问答全文搜索能力优化</b>
                ：引入多阶段全文搜索策略，弥补传统向量检索不足，显著提升命中精准度。
              </li>
              <li>
                <b>正文与附件索引区分</b>
                ：优化政策问答全文索引，对正文与附件进行精细化拆分，附件匹配能力进一步增强。
              </li>
              <li>
                <b>超大附件上下文处理优化</b>
                ：针对超大附件内容引入压缩与关键段落提取算法，提升附件检索命中率。
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.1.6</Title>
            <Paragraph className={styles.date}>2025年5月9日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>政策数据库扩充</b>
                ：新增最新医疗政策数据，覆盖范围更广，提供更全面的行业洞察。
              </li>
              <li>
                <b>界面显示优化</b>
                ：修复多处显示异常问题，提升整体用户体验。
              </li>
              <li>
                <b>搜索性能提升</b>
                ：优化搜索算法，提高检索速度和准确率。
              </li>
              <li>
                <b>政策问答样式优化</b>
                ：优化了政策问答界面的视觉呈现。
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.1.5</Title>
            <Paragraph className={styles.date}>2025年5月4日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>相关政策推荐优化</b>
                ：优化了相关政策推荐算法，结果更精准，响应更快。
              </li>
              <li>
                <b>架构重构与性能提升</b>
                ：对前后端架构进行整体重构，显著提升页面渲染速度和数据拉取效率。
              </li>
              <li>
                <b>缓存机制引入</b>
                ：增加了缓存机制，提升常用查询的响应速度。
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.1.4</Title>
            <Paragraph className={styles.date}>2025年4月30日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>政策问答增强</b>
                ：强化精准检索能力，新增对时间段、文号、索引号的智能识别，您可通过自然语言随意组合查询条件，轻松获取所需政策信息，如：
                <ul>
                  <li>去年开春有过哪些重要政策？</li>
                  <li>聊聊食安办函 〔 2025 〕 14 号</li>
                  <li>聊聊11100000MB0143028R/2025-810697</li>
                  <li>食安办函 〔 2025 〕 14 号和国市监稽函〔2025〕37号对比</li>
                  <li>
                    11100000MB0143028R/2025-915493与11100000MB0143028R/2025-810697对比
                  </li>
                </ul>
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.1.3</Title>
            <Paragraph className={styles.date}>2025年4月22日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>政策文件属性增强</b>
                ：新增政策文件重要程度、文件性质、相关度元素，支持多维度筛选，帮助用户更精准地定位所需政策信息。
              </li>
              <li>
                <b>政策影响力分析</b>
                ：引入政策影响力分析图表功能，支持单篇政策分析和多政策对比分析，直观展示政策影响范围和程度，辅助用户进行更全面的政策评估。
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.1.2</Title>
            <Paragraph className={styles.date}>2025年4月20日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>工作台页面</b>
                ：推出全新工作台功能，集成核心数据和操作于一体，使用户能够一目了然掌握关键信息并快速进行常用操作，提升工作效率。
              </li>
              <li>
                <b>附件支持全面升级</b>
                ：全面整合附件功能到核心业务流程：
                <ul>
                  <li>政策查询支持标题和全文范围内的附件内容检索</li>
                  <li>智能问答系统支持解读附件内容</li>
                  <li>政策解读和对比功能支持分析附件文档</li>
                  <li>支持多种格式附件的在线预览和下载</li>
                </ul>
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.1.1</Title>
            <Paragraph className={styles.date}>2025年4月18日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>收藏功能</b>
                ：新增政策收藏功能，方便用户保存和快速访问关注的政策内容。
              </li>
              <li>
                <b>检索优化</b>
                ：优化了检索正确率，增加标题的向量检索，使搜索结果更加精准。
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.1.0</Title>
            <Paragraph className={styles.date}>2025年4月11日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>政策源扩展</b>
                ：整合更多权威政策来源，提供更全面的政策覆盖范围，助力用户获取多维度政策信息。
              </li>
              <li>
                <b>界面优化</b>
                ：重新设计政策问答界面，提升用户交互体验，使问答流程更加直观高效。
              </li>
              <li>
                <b>搜索增强</b>
                ：改进搜索算法，提供更精准的政策匹配结果，减少筛选时间，提高检索效率。
              </li>
              <li>
                <b>性能提升</b>
                ：优化后台处理逻辑，加快数据加载速度，提升整体平台响应能力。
              </li>
            </ul>
          </div>

          <div className={styles.section}>
            <Title heading={4}>版本 1.0.0</Title>
            <Paragraph className={styles.date}>2025年3月25日</Paragraph>
            <ul className={styles.list}>
              <li>
                <b>政策查询</b>：提供全文查询功能，获取全面政策信息。
              </li>
              <li>
                <b>政策解读</b>：AI 驱动的政策解读与政策对比功能。
              </li>
              <li>
                <b>政策问答</b>：AI 智能问答系统，解答政策相关疑问。
              </li>
              <li>
                <b>政策监控</b>
                ：支持订阅关键词、监控源设置、通知频率调整、内容列表查看和分析报告生成。
              </li>
            </ul>
          </div>

          <div className={styles.footer}>
            <Text>
              如需了解本版本的更多详情，欢迎通过邮箱 hi@suwendata.com
              联系我们的客户服务团队。
            </Text>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default Changelog;
